<template>
  <div class="result-display">
    <!-- 背景装饰元素 -->
    <div class="background-elements">
      <div class="bg-circle bg-circle-1"></div>
      <div class="bg-circle bg-circle-2"></div>
      <div class="bg-circle bg-circle-3"></div>
    </div>
    
    <!-- 主容器 -->
    <div class="result-container">
      <!-- 头部区域 -->
      <div class="result-header">
        <!-- 返回按钮 -->
        <button @click="handleBack" class="back-button">
          <i class="fa fa-chevron-left"></i>
          <span class="back-text">{{ $t('result.backButton') }}</span>
        </button>
        
        <!-- 标题 -->
        <div class="result-title">
          <div class="title-text">{{ $t('result.title') }}</div>
        </div>
        
        <!-- 语言选择器 -->
        <div class="language-selector-wrapper">
          <select v-model="selectedLanguage" @change="changeLanguage" class="language-selector">
            <option value="zh">中文</option>
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>
          <i class="fa fa-globe language-icon"></i>
        </div>
      </div>
      
      <!-- 日期导航 -->
      <div class="date-navigation">
        <div class="date-nav">
          <div v-for="(day, index) in dateNav" :key="index"
               class="date-item"
               :class="{ 
                 'date-today': day.isToday,
                 'date-selected': day.isSelected,
                 'date-weekend': day.dayOfWeek === $t('result.sun') || day.dayOfWeek === $t('result.sat')
               }"
               @click="selectDate(day.date)">
            <div class="date-weekday">{{ day.dayOfWeek }}</div>
            <div class="date-day">{{ day.dayNumber }}</div>
          </div>
        </div>
      </div>
      
      <!-- 加载指示器 -->
      <div v-if="isLoading" class="loading-overlay">
        <div class="loading-content">
          <div class="loading-spinner"></div>
          <div class="loading-text">{{ $t('result.loading') || '加载中...' }}</div>
        </div>
      </div>
      
      <!-- 主要内容区域 -->
      <div class="result-content">
        <!-- 综合评分与指标合并卡片 -->
        <div class="combined-score-indicators-card">
          <div class="combined-card-header">
            <div class="combined-card-title">
              <i class="fa fa-star card-icon"></i>
              <span>{{ $t('result.compositeScore') || '综合评分' }}</span>
            </div>

          </div>
          <div class="combined-card-content">
            <!-- 左侧：综合评分 -->
            <div class="combined-score-section">
              <div class="modern-score-display">
                <div class="modern-score-container">
                  <div class="modern-score-value">{{ compositeScore }}</div>
                  <div class="modern-score-label">{{ t('result.scoreLabel') || '综合评分' }}</div>
                  <div class="modern-score-stars">
                    <i class="fa fa-star" v-for="n in 5" :key="n" :class="{ 'active': n <= Math.ceil(compositeScore / 20) }"></i>
                  </div>
                  <div class="modern-score-bg">
                    <div class="modern-score-bg-circle circle-1"></div>
                    <div class="modern-score-bg-circle circle-2"></div>
                    <div class="modern-score-bg-circle circle-3"></div>
                  </div>
                </div>
              </div>

            </div>
            
            <!-- 右侧：树形柱状图指标 -->
            <div class="combined-indicators-section">
              <div class="tree-bar-chart">
                <div v-for="(indicator, index) in indicators" :key="index" class="tree-bar-item">
                  <div class="tree-bar-value">{{ indicator.value }}</div>
                  <div class="tree-bar-container">
                    <div class="tree-bar-fill" :style="{ height: indicator.value + '%' }" :class="indicator.color"></div>
                    <div class="tree-bar-grid">
                      <div class="tree-bar-grid-line" v-for="n in 5" :key="n"></div>
                    </div>
                  </div>
                  <div class="tree-bar-label-container">
                    <div class="tree-bar-icon" :class="indicator.color">
                      <i :class="getIndicatorIcon(indicator.label)"></i>
                    </div>
                    <div class="tree-bar-label">{{ indicator.label }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 五行分析卡片 -->
        <div class="wuxing-card" :class="{
          'fire-background': weakestWuxing === '火',
          'water-background': weakestWuxing === '水',
          'metal-background': weakestWuxing === '金',
          'wood-background': weakestWuxing === '木',
          'earth-background': weakestWuxing === '土'
        }">
          <!-- 雨滴效果元素 -->
          <div class="rain-drop"></div>
          <div class="rain-drop"></div>
          <div class="rain-drop"></div>
          <div class="rain-drop"></div>
          <div class="rain-drop"></div>
          <div class="rain-drop"></div>
          <div class="rain-drop"></div>
          <div class="rain-drop"></div>
          <div class="rain-drop"></div>
          
          <!-- 水珠下滑效果元素 -->
          <div class="water-drop water-drop-1"></div>
          <div class="water-drop water-drop-2"></div>
          <div class="water-drop water-drop-3"></div>
          <div class="water-drop water-drop-4"></div>
          <div class="water-drop water-drop-5"></div>
          
          <!-- 树叶飘落效果元素（木元素） -->
          <div class="leaf"></div>
          <div class="leaf"></div>
          <div class="leaf"></div>
          <div class="leaf"></div>
          <div class="leaf"></div>
          
          <!-- 沙粒流动效果元素（土元素） -->
          <div class="sand-particle"></div>
          <div class="sand-particle"></div>
          <div class="sand-particle"></div>
          <div class="sand-particle"></div>
          <div class="sand-particle"></div>
          <div class="sand-particle"></div>
          
            <div class="card-header">
            <div class="card-title">
              <i class="fa fa-yin-yang card-icon"></i>
              <span>{{ $t('result.wuxingAnalysis') || '五行分析' }}</span>
            </div>
          </div>
          <div class="wuxing-content">

            <div class="wuxing-summary">
              <div class="summary-item">
                <div class="summary-label">{{ $t('result.daymasterWuxing') || '日主五行' }}:</div>
                <div class="summary-value">{{ translateWuxing(daymasterWuxing) }}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">{{ $t('result.strongestWuxing') || '最强五行' }}:</div>
                <div class="summary-value">{{ translateWuxing(strongestWuxing) }}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">{{ $t('result.weakestWuxing') || '最弱五行' }}:</div>
                <div class="summary-value">{{ translateWuxing(weakestWuxing) }}</div>
              </div>
              <div class="summary-text">{{ $t('result.weakestExplain0') }}</div>
              <div class="summary-text">{{ $t('result.weakestExplain1') }}</div>
              <div class="summary-text">{{ $t('result.weakestExplain2') }}</div>
              <div class="summary-text">{{ $t('result.weakestExplain3') }}</div>
              <div class="summary-text">{{ $t('result.weakestExplain4') }}</div>
              <div class="summary-text">{{ $t('result.weakestExplain5') }}</div>
            </div>
          </div>
        </div>
        
        <!-- 今日运势卡片 -->
        <div class="fortune-card">
          <div class="card-header">
            <div class="card-title">
              <i class="fa fa-comment-dots card-icon"></i>
              <span>{{ $t('result.todayFortune') }}</span>
            </div>
          </div>
          <div class="fortune-content">
            <div class="fortune-text" v-html="fortuneText"></div>
          </div>
        </div>
        
        <!-- 幸运元素卡片 -->
        <div class="lucky-card">
          <div class="card-header">
            <div class="card-title">
              <i class="fa fa-gem card-icon"></i>
              <span>{{ $t('result.luckyElements') || '幸运元素' }}</span>
            </div>
          </div>
          <div class="lucky-grid">
            <div class="lucky-item">
              <div class="lucky-icon direction">
                <i class="fas fa-compass"></i>
              </div>
              <div class="lucky-info">
                <div class="lucky-value">{{ luckyDirection }}</div>
                <div class="lucky-label">{{ $t('result.luckyDirection') || '幸运方向' }}</div>
              </div>
            </div>
            <div class="lucky-item">
              <div class="lucky-icon number">
                <i class="fas fa-dice"></i>
              </div>
              <div class="lucky-info">
                <div class="lucky-value">{{ luckyNumber }}</div>
                <div class="lucky-label">{{ $t('result.luckyNumber') || '幸运数字' }}</div>
              </div>
            </div>
            <div class="lucky-item">
              <div class="lucky-icon constellation">
                <i class="fas fa-star"></i>
              </div>
              <div class="lucky-info">
                <div class="lucky-value">{{ luckyConstellation }}</div>
                <div class="lucky-label">{{ $t('result.luckyConstellation') || '幸运星座' }}</div>
              </div>
            </div>
            <div class="lucky-item">
              <div class="lucky-icon color">
                <i class="fas fa-palette"></i>
              </div>
              <div class="lucky-info">
                <div class="lucky-value">{{ luckyColor }}</div>
                <div class="lucky-label">{{ $t('result.luckyColor') || '幸运颜色' }}</div>
              </div>
            </div>
          </div>
        </div>
        <!-- 五行音乐卡片 -->
        <div class="wuxing-music-card">
          <div class="card-header">
            <div class="card-title">
              <i class="fa fa-music card-icon"></i>
              <span>{{ $t('result.wuxingMusic') || '五行音乐' }}</span>
              
            </div>
          </div>
          <div class="wuxing-music-content">
            <div class="music-info">
              <div class="music-description">
                {{ $t('result.marquee.general') }}
              </div>
              <div class="music-description">
                {{ currentMusicTitle }}：{{ wuxingMusicDescription }}
              </div>
            </div>

            <!-- 内联音乐播放器 -->
            <div class="inline-music-player" :class="currentWuxingTheme">
              <div class="player-header">
                <div class="player-title">{{ currentMusicTitle }}</div>
                <div class="player-status" :class="{ 'playing': isPlaying }">
                  <i :class="isPlaying ? 'fa fa-volume-up' : 'fa fa-volume-off'"></i>
                  <span class="status-text">{{ isPlaying ? $t('music.playing') : $t('music.paused') }}</span>
                </div>
              </div>
              <div class="player-controls-inline">
                <button @click="togglePlay" class="control-btn-inline play-btn-inline" :class="{ 'playing': isPlaying }" :title="isPlaying ? $t('music.pause') : $t('music.play')">
                  <i :class="isPlaying ? 'fa fa-pause' : 'fa fa-play'"></i>
                </button>
                <button @click="stopMusic" class="control-btn-inline stop-btn-inline" :title="$t('music.stop')">
                  <i class="fa fa-stop"></i>
                </button>
                <button @click="toggleAutoPlay" class="control-btn-inline autoplay-btn-inline" :class="{ 'autoplay-active': autoPlayEnabled }" :title="autoPlayEnabled ? $t('music.autoplayOn') : $t('music.autoplayOff')">
                  <i :class="autoPlayEnabled ? 'fa fa-toggle-on' : 'fa fa-toggle-off'"></i>
                </button>
                <div class="volume-control-inline">
                  <i class="fa fa-volume-up volume-icon-inline"></i>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    v-model="volume"
                    @input="updateVolume"
                    class="volume-slider-inline"
                  />
                  <span class="volume-value">{{ volume }}%</span>
                </div>
              </div>
            </div>
            
            <!-- 音频元素 -->
            <audio
              ref="audioPlayer"
              :src="currentMusicUrl"
              @ended="onMusicEnded"
              @timeupdate="onTimeUpdate"
            ></audio>

          </div>
        </div>
        <!-- 幸运推荐卡片 -->
        <div class="bracelets-card">
          <div class="card-header">
            <div class="card-title">
              <i class="fa fa-gem card-icon"></i>
              <span>{{ $t('result.luckyBracelets') || '幸运推荐' }}</span>
            </div>
          </div>
          <div class="bracelets-content">
            <div class="carousel-container">
              <div class="carousel" :style="{ transform: `translateX(-${currentBraceletIndex * 100}%)` }">
                <div v-for="(bracelet, index) in bracelets" :key="index"
                     class="carousel-item"
                     @click="openBraceletLink(bracelet.url)">
                  <div class="bracelet-image-wrap">
                    <div class="bracelet-image">
                      <img :src="bracelet.image" :alt="bracelet.name" />
                    </div>
                  </div>
                  <div class="bracelet-name">{{ bracelet.name }}</div>
                </div>
              </div>
              <button @click="prevSlide" class="carousel-button prev">
                <i class="fa fa-chevron-left"></i>
              </button>
              <button @click="nextSlide" class="carousel-button next">
                <i class="fa fa-chevron-right"></i>
              </button>
              <div class="carousel-indicators">
                <div v-for="(bracelet, index) in bracelets" :key="index"
                     class="indicator"
                     :class="{ active: currentBraceletIndex === index }"
                     @click="goToSlide(index)"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- 邮箱保存卡片 - 位于轮播图下方 -->
        <div class="email-save-card">
          <div class="card-header">
            <div class="card-title">
              <i class="fa fa-envelope card-icon"></i>
              <span>{{ $t('result.emailSaveTitle') || '保存结果' }}</span>
            </div>
          </div>
          <div class="email-save-content">
            <div class="email-save-description">
              {{ $t('result.emailSaveDescription') }}
            </div>
            <div class="email-input-group">
              <input
                type="email"
                v-model="userEmail"
                :placeholder="$t('result.emailPlaceholder')"
                class="email-input"
                :disabled="emailSaved"
              />
              <button
                @click="saveEmail"
                class="save-email-btn"
                :disabled="isSavingEmail || !isValidEmail || emailSaved"
                :class="{ 'saved': emailSaved }"
              >
                <span v-if="emailSaved">
                  <i class="fa fa-check"></i>
                  {{ $t('result.emailSaved') || '联系方式' }}
                </span>
                <span v-else-if="isSavingEmail">
                  <i class="fa fa-spinner fa-spin"></i>
                  {{ $t('result.saving') || '保存中...' }}
                </span>
                <span v-else>
                  {{ $t('result.saveEmail') || '提交' }}
                </span>
              </button>
            </div>
            <div v-if="emailSaveMessage" class="email-save-message" :class="emailSaveStatus">
              <i :class="emailSaveStatus === 'success' ? 'fa fa-check-circle' : 'fa fa-exclamation-circle'"></i>
              {{ emailSaveMessage }}
            </div>
          </div>
        </div>

      </div>
    </div>
</div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import './ResultDisplay.css'

const { t, locale } = useI18n()
const router = useRouter()

// 组件挂载状态
const isComponentMounted = ref(false)
onMounted(() => {
  isComponentMounted.value = true
})

// 邮箱保存相关
const userEmail = ref('')
const isSavingEmail = ref(false)
const emailSaved = ref(false)
const emailSaveMessage = ref('')
const emailSaveStatus = ref('')
const userId = ref(localStorage.getItem('userId') || '')

// 邮箱验证
const isValidEmail = computed(() => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(userEmail.value)
})

// 获取基础API URL（SunCoreOline后端）
const getBaseApiUrl = () => {
  const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

  if (isDevelopment) {
    return 'http://localhost:5042'
  }
  // 生产环境使用相对路径（避免跨域问题）
  return ''
}

// 保存邮箱
const saveEmail = async () => {
  if (!isValidEmail.value) {
    emailSaveMessage.value = t('result.invalidEmail') || '请输入有效的邮箱地址'
    emailSaveStatus.value = 'error'
    return
  }

  if (!userId.value) {
    emailSaveMessage.value = '用户ID不存在，请重新提交表单'
    emailSaveStatus.value = 'error'
    return
  }

  isSavingEmail.value = true
  emailSaveMessage.value = ''

  try {
    const response = await fetch(getBaseApiUrl() + '/api/fortune/user/update-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userId: userId.value,
        email: userEmail.value
      })
    })

    const result = await response.json()

    if (result.success) {
      emailSaved.value = true
      emailSaveMessage.value = t('result.emailSaveSuccess') 
      emailSaveStatus.value = 'success'
    } else {
      emailSaveMessage.value = result.message || t('result.emailSaveError') 
      emailSaveStatus.value = 'error'
    }
  } catch (error) {
    console.error('保存邮箱失败:', error)
    emailSaveMessage.value = t('result.emailSaveError') || '保存失败，请稍后重试'
    emailSaveStatus.value = 'error'
  } finally {
    isSavingEmail.value = false
  }
}

// 复制用户ID
const copyUserId = () => {
  if (!userId.value) return

  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(userId.value).then(() => {
      alert(t('result.copySuccess') || '已复制到剪贴板')
    }).catch(() => {
      fallbackCopy(userId.value)
    })
  } else {
    fallbackCopy(userId.value)
  }
}

// 复制降级方案
const fallbackCopy = (text) => {
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.select()
  try {
    document.execCommand('copy')
    alert(t('result.copySuccess') || '已复制到剪贴板')
  } catch (err) {
    alert(t('result.copyFailed') || '复制失败')
  }
  document.body.removeChild(textarea)
}

// 翻译五行名称
const translateWuxing = (wuxingName) => {
  if (!wuxingName) return ''
  
  // 检查翻译结果是否有效（不包含'wuxingNames'，且不是空字符串）
  const isValidTranslation = (translation) => {
    return translation &&
           translation !== '' &&
           !translation.includes('wuxingNames') &&
           !translation.startsWith('result.')
  }
  
  // 首先尝试直接查找
  let translated = t(`result.wuxingNames.${wuxingName}`)
  if (isValidTranslation(translated)) {
    return translated
  }
  
  // 尝试小写版本
  const lowerCaseName = wuxingName.toLowerCase()
  if (lowerCaseName !== wuxingName) {
    translated = t(`result.wuxingNames.${lowerCaseName}`)
    if (isValidTranslation(translated)) {
      return translated
    }
  }
  
  // 映射各种可能的五行名称到标准键
  const wuxingMap = {
    // 中文
    '金': 'metal',
    '木': 'wood',
    '水': 'water',
    '火': 'fire',
    '土': 'earth',
    
    // 英文（各种形式）
    'metal': 'metal',
    'wood': 'wood',
    'water': 'water',
    'fire': 'fire',
    'earth': 'earth',
    'Metal': 'metal',
    'Wood': 'wood',
    'Water': 'water',
    'Fire': 'fire',
    'Earth': 'earth',
    
    // 西班牙语
    'madera': 'wood',
    'agua': 'water',
    'fuego': 'fire',
    'tierra': 'earth',
    'Madera': 'wood',
    'Agua': 'water',
    'Fuego': 'fire',
    'Tierra': 'earth',
    
    // 西班牙语五行名称的直接映射（用于西班牙语环境）
    'Madera': 'Madera',
    'Agua': 'Agua',
    'Fuego': 'Fuego',
    'Tierra': 'Tierra',
    'madera': 'madera',
    'agua': 'agua',
    'fuego': 'fuego',
    'tierra': 'tierra'
  }
  
  // 查找映射键
  const mappedKey = wuxingMap[wuxingName] || wuxingMap[lowerCaseName]
  if (mappedKey) {
    // 首先尝试使用映射键查找
    translated = t(`result.wuxingNames.${mappedKey}`)
    if (isValidTranslation(translated)) {
      return translated
    }
    
    // 如果映射键查找失败，尝试直接使用原名称（可能是西班牙语名称）
    translated = t(`result.wuxingNames.${wuxingName}`)
    if (isValidTranslation(translated)) {
      return translated
    }
  }
  
  // 如果所有尝试都失败，返回原名称（但确保不是包含'wuxingNames'的字符串）
  if (wuxingName.includes('wuxingNames') || wuxingName.startsWith('result.')) {
    // 提取最后一个点之后的部分作为回退
    const parts = wuxingName.split('.')
    return parts[parts.length - 1] || wuxingName
  }
  
  return wuxingName
}

// 音乐播放器相关逻辑
const audioPlayer = ref(null)
const isPlaying = ref(false) // 初始不播放
const volume = ref(70)
const playerVisible = ref(true)
const autoPlayAttempted = ref(false)
const autoPlayEnabled = ref(false) // 自动播放开关，默认关闭

// 获取音乐文件URL
const getMusicUrl = (fileName) => {
  return `https://pub-8d1dbd7631b646fa80a1fbfe3640ce5b.r2.dev/music/${fileName}`;
  //return `https://pub-8d1dbd7631b646fa80a1fbfe3640ce5b.r2.dev/music/tu.mp3`;
}

// 五行音乐映射
const wuxingMusicMap = {
  '金': getMusicUrl('jin.mp3'),
  '木': getMusicUrl('mu.mp3'),
  '水': getMusicUrl('shui.mp3'),
  '火': getMusicUrl('huo.mp3'),
  '土': getMusicUrl('tu.mp3'),
  'Metal': getMusicUrl('jin.mp3'),
  'Wood': getMusicUrl('mu.mp3'),
  'Water': getMusicUrl('shui.mp3'),
  'Fire': getMusicUrl('huo.mp3'),
  'Earth': getMusicUrl('tu.mp3')
}

// 五行音乐名称映射
const wuxingMusicNameMap = {
  '金': '金行能量音乐',
  '木': '木行能量音乐',
  '水': '水行能量音乐',
  '火': '火行能量音乐',
  '土': '土行能量音乐',
  'metal': 'Metal Energy Music',
  'wood': 'Wood Energy Music',
  'water': 'Water Energy Music',
  'fire': 'Fire Energy Music',
  'earth': 'Earth Energy Music'
}

// 当前播放的音乐（基于最弱五行）
const currentMusicUrl = computed(() => {
  const wuxing = weakestWuxing.value
  console.log('当前五行（最弱）：', wuxingMusicMap[wuxing])
  return wuxingMusicMap[wuxing] || wuxingMusicMap['水']
})

const currentMusicName = computed(() => {
  const wuxing = weakestWuxing.value
  return wuxingMusicNameMap[wuxing] || wuxingMusicNameMap['水']
})

// 音乐标题（显示完整的五行音乐名称）
const currentMusicTitle = computed(() => {
  const wuxing = weakestWuxing.value
  if (!wuxing) return t('result.musicPlayer') || '五行能量音乐'
    console.log('当前五行（最弱）2：', wuxing)
  // 根据当前语言显示五行名称
  const wuxingMap = {
    '金': { zh: '金', en: 'Metal', es: 'Metal' },
    '木': { zh: '木', en: 'Wood', es: 'Madera' },
    '水': { zh: '水', en: 'Water', es: 'Agua' },
    '火': { zh: '火', en: 'Fire', es: 'Fuego' },
    '土': { zh: '土', en: 'Earth', es: 'Tierra' },
    'Metal': { zh: '金', en: 'Metal', es: 'Metal' },
    'Wood': { zh: '木', en: 'Wood', es: 'Madera' },
    'Water': { zh: '水', en: 'Water', es: 'Agua' },
    'Fire': { zh: '火', en: 'Fire', es: 'Fuego' },
    'Earth': { zh: '土', en: 'Earth', es: 'Tierra' },
    'Madera': { zh: '木', en: 'Wood', es: 'Madera' },
    'Agua': { zh: '水', en: 'Water', es: 'Agua' },
    'Fuego': { zh: '火', en: 'Fire', es: 'Fuego' },
    'Tierra': { zh: '土', en: 'Earth', es: 'Tierra' }
  }

  const lang = locale.value
  const mapping = wuxingMap[wuxing] || wuxingMap['水']
  const wuxingName = mapping[lang] || mapping.zh
     console.log('当前五行（最弱）wuxingName：', wuxingName)
  // 根据语言返回完整的音乐名称
  if (lang === 'en') {
    return `${wuxingName} Energy Music`
  } else if (lang === 'es') {
    return `Música de Energía ${wuxingName}`
  } else {
    // 中文：五行名称 + "能量音乐"
    return `${wuxingName}能量音乐`
  }
})

// 五行音乐描述文本（原滚动字幕内容）
const wuxingMusicDescription = computed(() => {
  const wuxing = weakestWuxing.value
  console.log('当前五行（最弱）1：', wuxing)
  // 如果没有五行信息，返回通用描述
  if (!wuxing) {
    return t('result.marquee.general', { wuxing: translateWuxing(wuxing) }) || '根据你的生辰八字，我们会算出你五行中哪一种偏弱、哪一种过盛。针对"缺"或"弱"的那一行，通过特定频率的音乐来补益，让能量回归平衡。'
  }
  // 五行特定描述
  const wuxingKeyMap = {
    '金': 'metal',
    '木': 'wood',
    '水': 'water',
    '火': 'fire',
    '土': 'earth',
    'Metal': 'metal',
    'Wood': 'wood',
    'Water': 'water',
    'Fire': 'fire',
    'Earth': 'earth',
    'Metal': 'metal',
    'Madera': 'wood',
    'Agua': 'water',
    'Fuego': 'fire',
    'Tierra': 'earth'
  }
  
  const wuxingKey = wuxingKeyMap[wuxing]
  const translationKey = `result.marquee.${wuxingKey}`
  // 尝试获取翻译
  const translatedText = t(translationKey, { wuxing: translateWuxing(wuxing) })
  
  // 如果翻译有效（不是键本身），返回翻译
  if (translatedText &&
      translatedText !== translationKey &&
      !translatedText.includes('result.marquee')) {
    return translatedText
  }
  
  // 回退到通用描述
  return t('result.marquee.general', { wuxing: translateWuxing(wuxing) }) || '根据你的生辰八字，我们会算出你五行中哪一种偏弱、哪一种过盛。针对"缺"或"弱"的那一行，通过特定频率的音乐来补益，让能量回归平衡。'
})


// 当前五行主题
const currentWuxingTheme = computed(() => {
  const wuxing = weakestWuxing.value
  if (!wuxing) return 'water-theme'
  
  const themeMap = {
    '金': 'metal-theme',
    '木': 'wood-theme',
    '水': 'water-theme',
    '火': 'fire-theme',
    '土': 'earth-theme',
    'metal': 'metal-theme',
    'wood': 'wood-theme',
    'water': 'water-theme',
    'fire': 'fire-theme',
    'earth': 'earth-theme'
  }
  
  return themeMap[wuxing] || 'water-theme'
})

// 播放/暂停音乐
const togglePlay = () => {
  if (!audioPlayer.value) return
  
  if (isPlaying.value) {
    audioPlayer.value.pause()
  } else {
    audioPlayer.value.play().catch(error => {
      console.error('播放音乐失败:', error)
    })
  }
  isPlaying.value = !isPlaying.value
}

// 停止音乐
const stopMusic = () => {
  if (!audioPlayer.value) return
  
  audioPlayer.value.pause()
  audioPlayer.value.currentTime = 0
  isPlaying.value = false
}

// 更新音量
const updateVolume = () => {
  if (audioPlayer.value) {
    audioPlayer.value.volume = volume.value / 100
  }
}

// 音乐播放结束
const onMusicEnded = () => {
  isPlaying.value = false
}

// 时间更新（用于进度条，如果需要的话）
const onTimeUpdate = () => {
  // 可以添加进度条逻辑
}

// 切换播放器显示/隐藏
const togglePlayer = () => {
  playerVisible.value = !playerVisible.value
  if (!playerVisible.value) {
    stopMusic()
    
  }
}

// 切换自动播放开关
const toggleAutoPlay = () => {
  autoPlayEnabled.value = !autoPlayEnabled.value
  console.log('自动播放开关:', autoPlayEnabled.value ? '开启' : '关闭')
  
  // 如果关闭自动播放且正在播放，停止音乐
  if (!autoPlayEnabled.value && isPlaying.value) {
    stopMusic()
  }
  
  // 保存设置到localStorage
  localStorage.setItem('autoPlayEnabled', autoPlayEnabled.value.toString())
}

// 翻译手串名称
const translateBraceletName = (braceletKey) => {
  return t(`result.bracelet.${braceletKey}`) || braceletKey
}

const props = defineProps({
  formData: {
    type: Object,
    default: null
  },
  fortuneData: {
    type: Object,
    default: null
  }
})

// 数据恢复逻辑
const localFormData = ref(props.formData)
const localFortuneData = ref(props.fortuneData)
const isLoading = ref(false)

// 如果props中没有数据，尝试从localStorage恢复
onMounted(() => {
  if (!localFormData.value || !localFortuneData.value) {
    const savedFormData = localStorage.getItem('fortuneFormData')
    const savedFortuneData = localStorage.getItem('fortuneData')
    
    if (savedFormData && savedFortuneData) {
      try {
        localFormData.value = JSON.parse(savedFormData)
        localFortuneData.value = JSON.parse(savedFortuneData)
        console.log('Data restored from localStorage')
      } catch (error) {
        console.error('Failed to parse saved data:', error)
      }
    } else {
      console.warn('No data found, redirecting to form')
      router.push('/form')
    }
  }
})

const emit = defineEmits(['back'])

// 语言切换
const selectedLanguage = ref(locale.value)
const changeLanguage = async () => {
  try {
    const currentLang = locale.value
    const targetLang = selectedLanguage.value
    
    if (currentLang === 'zh' && (targetLang === 'en' || targetLang === 'es')) {
      alert('请返回上一级切换语言')
      selectedLanguage.value = 'zh'
      isLoading.value = false
      return
    }
    
    if ((currentLang === 'en' || currentLang === 'es') && targetLang === 'zh') {
      let message = ''
      if (currentLang === 'es') {
        message = 'Vuelve al nivel anterior para cambiar de idioma'
      } else if (currentLang === 'en') {
        message = 'Go back to the previous level to change the language'
      }
      alert(message)
      selectedLanguage.value = currentLang
      isLoading.value = false
      return
    }
    
    isLoading.value = true
    locale.value = selectedLanguage.value
    // 保存语言选择到localStorage
    localStorage.setItem('userLocale', selectedLanguage.value)
    
    if (!localFormData.value) {
      console.error('No form data available for API call')
      isLoading.value = false
      return
    }
    
    const formatDate = (dateObj) => {
      const year = dateObj.getFullYear()
      const month = String(dateObj.getMonth() + 1).padStart(2, '0')
      const day = String(dateObj.getDate()).padStart(2, '0')
      return `${year}${month}${day}`
    }
    
    const predictionDate = formatDate(selectedDate.value)
    const { name, gender, birthDate } = localFormData.value
    
    const validation = validateBirthtime(birthDate, selectedLanguage.value)
    if (!validation.valid) {
      console.error('Invalid birth date format:', validation.message)
      isLoading.value = false
      return
    }
    const normalizedBirthDate = validation.normalizedDate || birthDate
    
    const apiResponse = await callFortuneAPI(name, gender, normalizedBirthDate, predictionDate, selectedLanguage.value)
    
    if (apiResponse.success && apiResponse.data) {
      localFortuneData.value = apiResponse.data
      localStorage.setItem('fortuneData', JSON.stringify(apiResponse.data))
      generateDateNav()
    } else {
      console.error('API response format error')
      alert('获取新语言运势数据失败，请稍后重试')
    }
  } catch (error) {
    console.error('Error in changeLanguage', error)
    alert(`获取新语言运势数据失败: ${error.message}`)
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  selectedLanguage.value = locale.value
})

// 日期导航
const selectedDate = ref(new Date())
const dateNav = ref([])

// 使用API数据
const compositeScore = computed(() => localFortuneData.value?.compositeScore || 65)

const indicators = computed(() => {
  const apiIndicators = localFortuneData.value?.indicators || []
  if (apiIndicators.length === 4) {
    return apiIndicators.map(indicator => ({
      label: indicator.label,
      value: indicator.value,
      color: indicator.color || getIndicatorColor(indicator.label)
    }))
  }
  
  return [
    { label: t('result.love'), value: 68, color: 'love' },
    { label: t('result.wealth'), value: 58, color: 'wealth' },
    { label: t('result.career'), value: 78, color: 'career' },
    { label: t('result.health'), value: 53, color: 'health' }
  ]
})

const getIndicatorColor = (label) => {
  const colorMap = {
    '爱情': 'love',
    'love': 'love',
    '财富': 'wealth',
    'wealth': 'wealth',
    '事业': 'career',
    'career': 'career',
    '健康': 'health',
    'health': 'health'
  }
  return colorMap[label] || 'love'
}

const getIndicatorIcon = (label) => {
  const iconMap = {
    '爱情': 'fa fa-heart',
    'love': 'fa fa-heart',
    '财富': 'fa fa-coins',
    'wealth': 'fa fa-coins',
    '事业': 'fa fa-briefcase',
    'career': 'fa fa-briefcase',
    '健康': 'fa fa-heartbeat',
    'health': 'fa fa-heartbeat'
  }
  return iconMap[label] || 'fa fa-chart-line'
}

const getScoreDescription = (score) => {
  if (score >= 80) return t('result.scoreExcellent') || '运势极佳，把握机会'
  if (score >= 60) return t('result.scoreGood') || '运势良好，稳步前行'
  if (score >= 40) return t('result.scoreFair') || '运势一般，注意调整'
  return t('result.scorePoor') || '运势不佳，保持耐心'
}

const getLevelClass = (score) => {
  if (score >= 80) return 'level-excellent'
  if (score >= 60) return 'level-good'
  if (score >= 40) return 'level-fair'
  return 'level-poor'
}

const getLevelText = (score) => {
  if (score >= 80) return '极佳'
  if (score >= 60) return '良好'
  if (score >= 40) return '一般'
  return '不佳'
}

// 五行分析
const wuxingData = computed(() => localFortuneData.value?.wuxingData || [
  { name: '金', score: 45, color: 'metal', barHeight: 45, personalScore: 32 },
  { name: '木', score: 68, color: 'wood', barHeight: 68, personalScore: 45 },
  { name: '水', score: 52, color: 'water', barHeight: 52, personalScore: 28 },
  { name: '火', score: 78, color: 'fire', barHeight: 78, personalScore: 65 },
  { name: '土', score: 38, color: 'earth', barHeight: 38, personalScore: 40 }
])

const strengthLevel = computed(() => localFortuneData.value?.strengthLevel || t('result.strengthLevel', { score: 68 }))
const strongestWuxing = computed(() => localFortuneData.value?.strongestWuxing || '水')
const weakestWuxing = computed(() => localFortuneData.value?.weakestWuxing || '水')
const daymasterWuxing = computed(() => localFortuneData.value?.daymasterWuxing || localFortuneData.value?.baziInfo?.daymasterWuxing || '水')

// 幸运元素
const luckyDirection = computed(() => localFortuneData.value?.luckyDirection || '东南')
const luckyNumber = computed(() => localFortuneData.value?.luckyNumber || '6')
const luckyConstellation = computed(() => localFortuneData.value?.luckyConstellation || '双子座')
const luckyColor = computed(() => localFortuneData.value?.luckyColor || '金黄色')

// 监听weakestWuxing变化，切换音乐
watch(weakestWuxing, (newWuxing, oldWuxing) => {
  if (newWuxing && newWuxing !== oldWuxing) {
    console.log(`最弱五行属性变化: ${oldWuxing} -> ${newWuxing}, 切换音乐`)
    
    // 如果正在播放，先暂停
    if (audioPlayer.value && !audioPlayer.value.paused) {
      audioPlayer.value.pause()
    }
    
    // 重置音频元素以加载新URL
    if (audioPlayer.value) {
      // audio元素的src属性通过currentMusicUrl计算属性自动更新
      // 我们需要重新加载音频
      audioPlayer.value.load()
      
      // 如果之前是播放状态且自动播放启用，尝试重新播放
      if (isPlaying.value && autoPlayEnabled.value) {
        // 由于浏览器策略，可能需要用户交互才能播放
        // 我们尝试播放，如果失败也没关系
        const playMusic = () => {
          audioPlayer.value.play().then(() => {
            console.log('五行切换后音乐播放成功')
          }).catch(err => {
            console.log('切换五行后自动播放被阻止:', err.message)
            // 如果播放失败，可能是因为缺少用户交互
            // 我们已经在selectDate中尝试过播放，所以这里不需要额外处理
          })
        }
        
        // 尝试立即播放
        playMusic()
        
        // 同时监听canplay事件，确保音频加载完成后尝试播放
        const onCanPlay = () => {
          playMusic()
          audioPlayer.value.removeEventListener('canplay', onCanPlay)
        }
        audioPlayer.value.addEventListener('canplay', onCanPlay)
        
        // 设置超时，防止事件未触发
        setTimeout(() => {
          audioPlayer.value.removeEventListener('canplay', onCanPlay)
        }, 3000)
      }
    }
  }
})

// 今日运势
const fortuneText = computed(() => {
  if (!localFortuneData.value) {
    return t('result.defaultFortune') || '今日运势平稳，适合处理日常事务。保持积极心态，会有不错的机会。'
  }

  const fortune = localFortuneData.value
  
  // 直接使用后端返回的 compositeScore，与顶部综合评分卡片保持一致
  const compositeScoreValue = fortune.compositeScore
  
  let enhancement = null
  const strengthMatch = (fortune.strengthLevel || '').match(/(\d+)/)
  if (strengthMatch) {
    enhancement = parseInt(strengthMatch[1])
  }
  
  const mainWuxing = fortune.strongestWuxing || null
  const weakestWuxing = fortune.weakestWuxing || null
  
  const goods = Array.isArray(fortune.goodActivities) ? fortune.goodActivities.slice(0, 3) : []
  const bads = Array.isArray(fortune.badActivities) ? fortune.badActivities.slice(0, 3) : []
  
  const luckyDir = fortune.luckyDirection || ''
  const luckyColorVal = fortune.luckyColor || ''
  const luckyConst = fortune.luckyConstellation || ''
  
  const avgLevel = compositeScoreValue >= 70 ? 'high' : compositeScoreValue >= 40 ? 'mid' : 'low'
  const enhLevel = enhancement >= 70 ? 'strong' : enhancement >= 40 ? 'medium' : 'weak'
  
  // 使用多语言翻译
  const suggestDo = goods.length ? t('result.suggestDo', { activities: goods.join(t('result.activitySeparator') || '、') }) : ''
  const suggestDont = bads.length ? t('result.suggestDont', { activities: bads.join(t('result.activitySeparator') || '、') }) : ''
  
  const luckyHintParts = [
    luckyDir ? t('result.luckyDirectionHint', { direction: luckyDir }) : '',
    luckyColorVal ? t('result.luckyColorHint', { color: luckyColorVal }) : '',
    luckyConst ? t('result.luckyConstellationHint', { constellation: luckyConst }) : '',
  ].filter(Boolean)
  
  const luckyHint = luckyHintParts.join(t('result.hintSeparator') || '；')
  
  // 根据enhLevel和avgLevel确定stance键
  let stanceKey = 'default'
  if (enhLevel === 'strong' && avgLevel === 'high') {
    stanceKey = 'strongHigh'
  } else if (enhLevel === 'strong' && avgLevel !== 'high') {
    stanceKey = 'strongOther'
  } else if (enhLevel === 'weak' && avgLevel === 'high') {
    stanceKey = 'weakHigh'
  } else if (enhLevel === 'weak' && avgLevel === 'low') {
    stanceKey = 'weakLow'
  } else if (enhLevel === 'strong') {
    stanceKey = 'strong'
  } else if (enhLevel === 'weak') {
    stanceKey = 'weak'
  } else if (enhLevel === 'medium') {
    stanceKey = 'medium'
  }
  
  const stance = t(`result.stance.${stanceKey}`) || t('result.stance.default') || '运势平稳，按计划行事即可'
  
  const wuxingLine = mainWuxing && enhancement
    ? t('result.wuxingLine', { wuxing: mainWuxing, score: enhancement })
    : ''
  
  const avgLine = t('result.avgLine', { score: compositeScoreValue })
  
  const weakestHint = weakestWuxing
    ? t('result.weakestHint', { wuxing: weakestWuxing })
    : ''
  
  // 构建独立行列表，每行一个信息
  const lines = []
  
  // 第1行：综合分数
  if (avgLine) lines.push(avgLine)
  
  // 第2行：五行气场
  if (wuxingLine) lines.push(wuxingLine)
  
  // 第3行：行动建议（stance）
  if (stance) lines.push(stance)
  
  // 第4行：最弱五行短板提示
  if (weakestHint) lines.push(weakestHint)
  
  // 第5行：幸运提示（方向、颜色、星座）
  if (luckyHint) lines.push(luckyHint)
  
  // 第6行：宜忌活动
  if (suggestDo || suggestDont) {
    const activitiesText = [suggestDo, suggestDont].filter(Boolean).join(' | ')
    lines.push(activitiesText)
  }
  
  return lines.join('<br>')
})

// 幸运手串
const getBraceletImageUrl = (imageName) => {
  return `https://pub-8d1dbd7631b646fa80a1fbfe3640ce5b.r2.dev/img/${imageName}`
  //return new URL(`../img/lunzhuan/${imageName}`, import.meta.url).href
}

// 轮播图数据，从后端接口获取
const bracelets = ref([])
const currentBraceletIndex = ref(0)
let slideInterval = null

// 日期导航生成
const generateDateNav = () => {
  const daysOfWeek = [t('result.sun'), t('result.mon'), t('result.tue'), t('result.wed'), t('result.thu'), t('result.fri'), t('result.sat')]
  const today = new Date()
  const nav = []
  
  for (let i = -3; i <= 3; i++) {
    const currentDate = new Date(today)
    currentDate.setDate(today.getDate() + i)
    
    const dayOfWeek = daysOfWeek[currentDate.getDay()]
    const dayNumber = currentDate.getDate()
    const isToday = i === 0
    const isSelected = currentDate.toDateString() === selectedDate.value.toDateString()
    
    nav.push({
      dayOfWeek,
      dayNumber,
      date: currentDate,
      isToday,
      isSelected
    })
  }
  
  dateNav.value = nav
}

// 选择日期
const selectDate = async (date) => {
  try {
    isLoading.value = true
    selectedDate.value = new Date(date)
    generateDateNav()
    
    // 在用户交互后立即尝试播放音乐（利用点击事件的用户交互上下文）
    // 仅在自动播放启用时才尝试播放音乐
    if (audioPlayer.value && autoPlayEnabled.value) {
      // 确保音频不是静音状态
      audioPlayer.value.muted = false
      // 设置播放状态为true
      isPlaying.value = true
      // 尝试播放当前音乐（利用用户交互上下文）
      audioPlayer.value.play().catch(err => {
        console.log('日期切换时播放音乐被阻止:', err.message)
        // 如果播放失败，可能是因为音频尚未加载完成或其他原因
        // 我们会在watch(strongestWuxing)中再次尝试
      })
    }
    
    if (!localFormData.value) {
      console.error('No form data available for API call')
      isLoading.value = false
      return
    }
    
    const formatDate = (dateObj) => {
      const year = dateObj.getFullYear()
      const month = String(dateObj.getMonth() + 1).padStart(2, '0')
      const day = String(dateObj.getDate()).padStart(2, '0')
      return `${year}${month}${day}`
    }
    
    const predictionDate = formatDate(selectedDate.value)
    const { name, gender, birthDate } = localFormData.value
    
    const validation = validateBirthtime(birthDate, selectedLanguage.value)
    if (!validation.valid) {
      console.error('Invalid birth date format:', validation.message)
      isLoading.value = false
      return
    }
    const normalizedBirthDate = validation.normalizedDate || birthDate
    
    const apiResponse = await callFortuneAPI(name, gender, normalizedBirthDate, predictionDate, selectedLanguage.value)
    
    if (apiResponse.success && apiResponse.data) {
      localFortuneData.value = apiResponse.data
      localStorage.setItem('fortuneData', JSON.stringify(apiResponse.data))
    } else {
      console.error('API response format error')
      alert('获取新日期运势数据失败，请稍后重试')
    }
  } catch (error) {
    console.error('Error in selectDate', error)
    alert(`获取新日期运势数据失败: ${error.message}`)
  } finally {
    isLoading.value = false
  }
}

// 轮播图功能
const goToSlide = (index) => {
  currentBraceletIndex.value = index
}

const nextSlide = () => {

  currentBraceletIndex.value = (currentBraceletIndex.value + 1) % bracelets.value.length
}

const prevSlide = () => {
  
  currentBraceletIndex.value = (currentBraceletIndex.value - 1 + bracelets.value.length) % bracelets.value.length
}

const startSlideInterval = () => {
  stopSlideInterval()
  slideInterval = setInterval(nextSlide, 3000)
}

const stopSlideInterval = () => {
  if (slideInterval) {
    clearInterval(slideInterval)
    slideInterval = null
  }
}

const openBraceletLink = (url) => {
  window.open(url, '_blank')
}

// 返回按钮
const handleBack = () => {
  localStorage.removeItem('fortuneFormData')
  localStorage.removeItem('fortuneData')
  router.push('/form')
}

// 验证出生时间格式
const validateBirthtime = (birthtime, language = 'zh') => {
  // 简化验证逻辑 - 接受任何已通过表单验证的格式
  if (!birthtime || !birthtime.toString().trim()) {
    return { valid: false, message: t('form.validation.invalidDate') }
  }
  
  const birthtimeStr = birthtime.toString().trim();
  
  // 如果是纯数字格式（8、10、12位），直接接受
  if (/^\d{8,12}$/.test(birthtimeStr)) {
    return { valid: true, normalizedDate: birthtimeStr }
  }
  
  // 如果不是纯数字，可能是原始日期字符串（如 "1988-01-01T12:00"）
  // 尝试解析日期，如果成功则返回原始字符串（API会处理格式化）
  try {
    const date = new Date(birthtimeStr);
    if (!isNaN(date.getTime())) {
      return { valid: true, normalizedDate: birthtimeStr }
    }
  } catch (error) {
    console.warn('日期解析失败，但继续尝试:', error);
  }
  
  // 如果以上都不行，返回错误
  return { valid: false, message: t('form.validation.invalidDate') }
}

// API调用函数
// 获取轮播图数据
const fetchBanners = async () => {
  try {
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const baseUrl = isDevelopment ? 'http://localhost:5042' : '';
    
    const response = await fetch(`${baseUrl}/api/fortune/banner/active?position=home`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (response.ok) {
      const result = await response.json();
      if (result.code === 200 && result.data && result.data.length > 0) {
        // 将后端数据转换为前端需要的格式
        bracelets.value = result.data.map(item => ({
          image: item.imageUrl,
          url: item.linkUrl || 'https://aiinovavida.com/'
        }));
        console.log('轮播图加载成功:', bracelets.value);
      } else {
        // 使用默认轮播图
        setDefaultBanners();
      }
    } else {
      // 使用默认轮播图
      setDefaultBanners();
    }
  } catch (error) {
    console.error('获取轮播图失败:', error);
    setDefaultBanners();
  }
};

// 设置默认轮播图
const setDefaultBanners = () => {
  bracelets.value = [
    { image: getBraceletImageUrl('111.jpg'), url: 'https://aiinovavida.com/' },
    { image: getBraceletImageUrl('222.jpg'), url: 'https://aiinovavida.com/' },
    { image: getBraceletImageUrl('333.jpg'), url: 'https://aiinovavida.com/' },
    { image: getBraceletImageUrl('444.jpg'), url: 'https://aiinovavida.com/' }
  ];
};

const callFortuneAPI = async (name, gender, birthDate, predictionDate, language = 'zh') => {
  try {
    // 检测当前环境
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const isHttps = window.location.protocol === 'https:';
    
    // 构建可用的API地址列表（按优先级排序）
    const apiCandidates = [];
    
    // 1. 相对路径（适用于生产环境Nginx代理和开发环境Vite代理，避免跨域问题）
    apiCandidates.push('/web-api/fortune/predict');
    
    // 2. 开发环境本地API
    if (isDevelopment) {
      apiCandidates.push('http://localhost:3001/web-api/fortune/predict');
      apiCandidates.push('http://127.0.0.1:3001/web-api/fortune/predict');
    }
    
    console.log('ResultDisplay API调用候选地址:', {
      isDevelopment,
      isHttps,
      hostname: window.location.hostname,
      candidates: apiCandidates,
      name, gender, birthDate, predictionDate, language
    });
    
    let lastError = null;
    
    // 按顺序尝试所有候选地址
    for (let i = 0; i < apiCandidates.length; i++) {
      const apiUrl = apiCandidates[i];
      console.log(`ResultDisplay 尝试API地址 [${i + 1}/${apiCandidates.length}]: ${apiUrl}`);
      
      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name,
            gender,
            birthDate,
            predictionDate,
            language
          })
        });
        
        console.log(`ResultDisplay API响应状态: ${response.status} ${response.statusText}`);
        
        if (response.ok) {
          const data = await response.json();
          console.log(`ResultDisplay API调用成功，使用地址: ${apiUrl}`);
          return data;
        } else {
          // 如果不是404，可能是其他错误，继续尝试下一个
          const errorText = await response.text();
          lastError = new Error(`API请求失败 (${response.status}): ${errorText.substring(0, 100)}`);
          console.warn(`ResultDisplay API地址 ${apiUrl} 失败:`, lastError.message);
          
          // 如果是404，继续尝试下一个
          if (response.status !== 404) {
            // 对于非404错误，可以提前停止尝试
            break;
          }
        }
      } catch (error) {
        lastError = error;
        console.warn(`ResultDisplay API地址 ${apiUrl} 网络错误:`, error.message);
        // 继续尝试下一个地址
      }
    }
    
    // 所有地址都尝试失败
    if (lastError) {
      throw lastError;
    } else {
      throw new Error('所有API地址尝试失败');
    }
    
  } catch (error) {
    console.error('ResultDisplay API调用错误详情:', {
      message: error.message,
      stack: error.stack,
      name: error.name,
      timestamp: new Date().toISOString()
    });
    
    // 提供更友好的错误信息
    let userMessage = error.message;
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      userMessage = '网络连接失败，请检查：1) API服务器是否运行 2) 网络设置 3) 防火墙设置';
    } else if (error.message.includes('NetworkError')) {
      userMessage = '网络错误，请确保您已连接到互联网';
    } else if (error.message.includes('CORS')) {
      userMessage = '跨域请求被阻止，请检查API服务器CORS配置';
    } else if (error.message.includes('404')) {
      userMessage = 'API接口未找到，请检查服务器配置';
    } else if (error.message.includes('混合内容')) {
      userMessage = '安全连接问题：HTTPS页面不能访问HTTP接口';
    }
    
    // 添加开发环境特定提示
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      userMessage += ' (开发环境提示：请确保API服务器运行在localhost:3001)';
    }
    
    // 添加微信浏览器特定提示
    const isWeChat = /MicroMessenger/i.test(navigator.userAgent);
    if (isWeChat && error.message.includes('Failed to fetch')) {
      userMessage = '微信浏览器网络限制，请尝试：1) 切换到系统浏览器 2) 检查网络代理设置';
    }
    
    const enhancedError = new Error(userMessage);
    enhancedError.originalError = error;
    enhancedError.isNetworkError = error.name === 'TypeError';
    throw enhancedError;
  }
}

// 生命周期
onMounted(() => {
  generateDateNav()
  startSlideInterval()
  
  // 获取轮播图数据
  fetchBanners()
  
  // 加载保存的邮箱和用户ID
  const savedEmail = localStorage.getItem('userEmail')
  const savedUserId = localStorage.getItem('userId')
  if (savedEmail) {
    userEmail.value = savedEmail
  }
  if (savedUserId) {
    userId.value = savedUserId
  }
  
  // 加载自动播放设置
  const savedAutoPlay = localStorage.getItem('autoPlayEnabled')
  if (savedAutoPlay !== null) {
    autoPlayEnabled.value = savedAutoPlay === 'true'
    console.log('加载自动播放设置:', autoPlayEnabled.value)
  }
  
  const carousel = document.querySelector('.carousel-container')
  if (carousel) {
    carousel.addEventListener('mouseenter', stopSlideInterval)
    carousel.addEventListener('mouseleave', startSlideInterval)
  }
  
  // 处理用户交互以启用音频播放
  const enableAudioPlayback = () => {
    if (!audioPlayer.value) return
    
    // 如果自动播放被禁用，不尝试播放
    if (!autoPlayEnabled.value) return
    
    console.log('用户交互，尝试播放音乐')
    // 确保音频不是静音状态
    audioPlayer.value.muted = false
    
    // 尝试播放
    audioPlayer.value.play().then(() => {
      console.log('用户交互后播放成功')
      isPlaying.value = true
      // 成功播放后移除所有监听器
      document.removeEventListener('click', enableAudioPlayback)
      document.removeEventListener('touchstart', enableAudioPlayback)
      document.removeEventListener('keydown', enableAudioPlayback)
    }).catch(err => {
      console.error('用户交互后播放失败:', err)
      // 如果仍然失败，可能是其他问题，我们不再重试
    })
  }
  
  // 添加用户交互监听器 - 尽早添加
  document.addEventListener('click', enableAudioPlayback)
  document.addEventListener('touchstart', enableAudioPlayback)
  document.addEventListener('keydown', enableAudioPlayback)
  
  // 也尝试自动播放（可能会失败）- 仅在自动播放启用时尝试
  const attemptAutoPlay = () => {
    if (audioPlayer.value && !autoPlayAttempted.value && autoPlayEnabled.value) {
      autoPlayAttempted.value = true
      
      console.log('尝试自动播放音乐，URL:', audioPlayer.value.src)
      audioPlayer.value.play().then(() => {
        console.log('音乐自动播放成功')
        isPlaying.value = true
        // 自动播放成功，移除用户交互监听器
        document.removeEventListener('click', enableAudioPlayback)
        document.removeEventListener('touchstart', enableAudioPlayback)
        document.removeEventListener('keydown', enableAudioPlayback)
      }).catch(error => {
        console.log('自动播放被浏览器阻止（预期行为）')
        // 这是预期行为，不需要记录为错误
      })
    }
  }
  
  // 延迟一点确保audio元素已加载，仅在自动播放启用时尝试
  if (autoPlayEnabled.value) {
    setTimeout(attemptAutoPlay, 500)
  }
})

onUnmounted(() => {
  stopSlideInterval()
  const carousel = document.querySelector('.carousel-container')
  if (carousel) {
    carousel.removeEventListener('mouseenter', stopSlideInterval)
    carousel.removeEventListener('mouseleave', startSlideInterval)
  }
})
</script>
